import { useEffect } from 'react';

import { useDraftSyncAndPublishContext } from '@confluence/native-collab/entry-points/useDraftSyncAndPublishContext.ts';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';

type LiveContributorsActivityOptions = {
	enabled: boolean;
	cloudId: string;
	contentId: string;
	contentType: string | null | undefined;
	onActivity: () => void;
};

export const useLiveContributorsActivity = ({
	enabled,
	cloudId,
	contentId,
	contentType,
	onActivity,
}: LiveContributorsActivityOptions): void => {
	const [{ collabEditProvider }] = useNativeCollabState();
	const { subscribeDraftSyncSuccess } = useDraftSyncAndPublishContext();

	// Both listeners are enabled only for live docs with demo polling off.
	// Classic pages use the separate pageUpdated subscription in CollaboratorsQueryProvider.
	useEffect(() => {
		if (!enabled || !collabEditProvider || !contentType) return;

		// The shared store can briefly retain the previous page's provider during navigation.
		const documentAri = `ari:cloud:confluence:${cloudId}:${contentType}/${contentId}`;
		if (collabEditProvider.getDocumentAri() !== documentAri) return;

		// Data includes acknowledged local edits, remote edits, and reconnect catch-up steps.
		collabEditProvider.on('data', onActivity);
		return () => {
			collabEditProvider.off('data', onActivity);
		};
	}, [enabled, collabEditProvider, cloudId, contentId, contentType, onActivity]);

	useEffect(() => {
		if (!enabled) return;

		return subscribeDraftSyncSuccess((syncedContentId) => {
			if (syncedContentId === contentId) onActivity();
		});
	}, [enabled, contentId, onActivity, subscribeDraftSyncSuccess]);
};
